import React from 'react';
import { useQuery } from 'react-query';
import { getBlogs } from '../../api/services';
import Blogs from './Blogs';
import { Box, Pagination } from '@mui/material';
import HeroBanner from '../Blogs/HeroBanner';

interface Blog {
  _id: string;
  title: string;
  description?: string;
  imageurl: string;
  posturl?: string;
  slug?: string;
  // ...other fields as needed
}

interface BlogsApiResponse {
  blogs?: Blog[];
  [key: string]: any;
}

const BLOGS_PER_PAGE = 6;

const BlogsPage: React.FC = () => {
    const { data, isLoading, isError, error } = useQuery<BlogsApiResponse, Error>(['blogs'], getBlogs);
    const [page, setPage] = React.useState(1);

    const blogs: Blog[] = React.useMemo(() => {
        if (!data || !Array.isArray(data.blogs)) return [];
        return data.blogs;
    }, [data]);

    const totalPages = Math.ceil(blogs.length / BLOGS_PER_PAGE);
    const paginatedBlogs = blogs.slice((page - 1) * BLOGS_PER_PAGE, page * BLOGS_PER_PAGE);

    const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (isLoading) return <div style={{color: '#ccc', textAlign: 'center', marginTop: 40}}>Loading...</div>;
    if (isError) return <div style={{color: '#f44336', textAlign: 'center', marginTop: 40}}>Error: {error?.message}</div>;

    return (
        <Box sx={{ pb: 6, }}>
            <HeroBanner />
            <Blogs data={{ blogs: paginatedBlogs }} />
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              mt={6}
              mb={4}
            >
                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    shape="rounded"
                    size="large"
                    siblingCount={1}
                    boundaryCount={1}
                    showFirstButton
                    showLastButton
                    sx={{
                      '& .MuiPaginationItem-root': {
                        color: '#bf742c',  // gold-ish color
                        fontWeight: 600,
                        fontSize: '1.1rem',
                        borderRadius: '8px',
                      },
                      '& .MuiPaginationItem-root:hover': {
                        backgroundColor: 'rgba(191, 116, 44, 0.2)', // subtle gold hover bg
                      },
                      '& .MuiPaginationItem-root.Mui-selected': {
                        background: 'linear-gradient(90deg, #bf742c 60%, #9f8054 100%)',
                        color: '#fff',
                        fontWeight: 'bold',
                        boxShadow: '0 0 8px rgba(191, 116, 44, 0.7)',
                      },
                      '& .MuiPaginationItem-root.Mui-selected:hover': {
                        background: 'linear-gradient(90deg, #9f8054 60%, #bf742c 100%)',
                        boxShadow: '0 0 10px rgba(191, 116, 44, 0.9)',
                      },
                      // Styling for first/last buttons
                      '& .MuiPaginationItem-previousNext': {
                        borderRadius: '50%',
                        color: '#bf742c',
                      }
                    }}
                />
            </Box>
        </Box>
    );
};

export default BlogsPage;
